/* RRTC Custom Print 2.2.0 — the existing product form owns the single submission. */
(() => {
  'use strict';
  const MAX_BYTES = 18 * 1024 * 1024; // Application limit leaves room for the lossless ZIP envelope.
  const MAX_PIXELS = 40_000_000;
  const hash = async b => [...new Uint8Array(await crypto.subtle.digest('SHA-256', b))].map(x => x.toString(16).padStart(2, '0')).join('');
  const crc32 = bytes => {
    let c = 0xffffffff;
    for (const b of bytes) { c ^= b; for (let i = 0; i < 8; i++) c = (c >>> 1) ^ ((c & 1) ? 0xedb88320 : 0); }
    return (c ^ 0xffffffff) >>> 0;
  };
  function pngDpi(buffer, dpi) {
    const b = new Uint8Array(buffer), v = new DataView(buffer);
    const chunks = [b.slice(0, 8)];
    let at = 8;
    while (at < b.length) {
      const n = v.getUint32(at), end = at + 12 + n;
      if (end > b.length) throw Error('Invalid PNG');
      const name = String.fromCharCode(...b.slice(at + 4, at + 8));
      if (name !== 'pHYs') chunks.push(b.slice(at, end));
      if (name === 'IHDR') {
        const c = new Uint8Array(21), d = new DataView(c.buffer), ppm = Math.round(dpi / .0254);
        d.setUint32(0, 9); c.set([112, 72, 89, 115], 4);
        d.setUint32(8, ppm); d.setUint32(12, ppm); c[16] = 1;
        d.setUint32(17, crc32(c.slice(4, 17))); chunks.push(c);
      }
      at = end;
    }
    const result = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
    let offset = 0; for (const c of chunks) { result.set(c, offset); offset += c.length; }
    return result;
  }
  function imageHeader(buffer) {
    const b = new Uint8Array(buffer), v = new DataView(buffer);
    if (b.length >= 24 && [137,80,78,71,13,10,26,10].every((n,i) => b[i] === n)) {
      // Animated PNG is deliberately excluded: its first frame is not an unambiguous order proof.
      for (let at=8; at+12<=b.length;) {
        const n=v.getUint32(at); if (at+12+n>b.length) throw Error('error_decode');
        if (String.fromCharCode(...b.slice(at+4,at+8)) === 'acTL') throw Error('error_type');
        at += n+12;
      }
      return {type:'image/png',w:v.getUint32(16),h:v.getUint32(20),orientation:1};
    }
    if (b[0] !== 255 || b[1] !== 216) throw Error('error_type');
    let at=2, w=0, h=0, orientation=1;
    while (at+4 <= b.length) {
      if (b[at++] !== 255) throw Error('error_decode');
      while (b[at] === 255) at++;
      const marker=b[at++];
      if (marker === 0xda || marker === 0xd9) break;
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      const n=v.getUint16(at); if (n<2 || at+n>b.length) throw Error('error_decode');
      if ([0xc0,0xc1,0xc2].includes(marker)) { h=v.getUint16(at+3); w=v.getUint16(at+5); }
      if (marker===0xe1 && n>=16 && String.fromCharCode(...b.slice(at+2,at+8))==='Exif\0\0') {
        try {
          const t=at+8, little=v.getUint16(t)===0x4949;
          if (v.getUint16(t+2,little)!==42) throw Error();
          const dir=t+v.getUint32(t+4,little), count=v.getUint16(dir,little);
          for(let i=0;i<count;i++) {
            const p=dir+2+i*12; if(p+12>at+n) break;
            if(v.getUint16(p,little)===0x112) orientation=v.getUint16(p+8,little);
          }
        } catch (_) { throw Error('error_decode'); }
      }
      at += n;
    }
    if (!w || !h || orientation<1 || orientation>8) throw Error('error_decode');
    return {type:'image/jpeg',w,h,orientation};
  }
  function layout(w, h, p, edit={zoom:1,x:0,y:0}) {
    const W=Number(p.w_mm), H=Number(p.h_mm), dpi=Number(p.dpi), margin=Number(p.margin_mm);
    if (![W,H,dpi,margin].every(Number.isFinite) || W<=0 || H<=0 || ![300,600].includes(dpi) || margin<0 || margin*2>=Math.min(W,H)) throw Error('error_config');
    const pw=Math.round(W/25.4*dpi), ph=Math.round(H/25.4*dpi);
    if (pw*ph>12_000_000) throw Error('error_config');
    const mx=Math.round(margin/W*pw), my=Math.round(margin/H*ph);
    const dest={x:mx,y:my,w:pw-2*mx,h:ph-2*my}, ratio=dest.w/dest.h;
    const {zoom,x,y}=edit;
    const minZoom=p.min_zoom===undefined?1:Number(p.min_zoom); // RRTC custom-case: optional smaller-than-area size
    if (!(minZoom>0 && minZoom<=1)) throw Error('error_config');
    if (![zoom,x,y].every(Number.isFinite) || zoom<minZoom || zoom>5 || Math.abs(x)>1 || Math.abs(y)>1) throw Error('error_config');
    const cw=Math.min(w,h*ratio)/zoom, rawH=cw/ratio, ch=Math.abs(rawH-h)<1e-8?h:rawH;
    const eff=Math.min(cw/((W-2*margin)/25.4),ch/((H-2*margin)/25.4));
    if (p.free_pan===true) { // RRTC custom-case: +-1 moves the image almost fully out of the area
      const kx=(cw+w)/2*0.9, ky=(ch+h)/2*0.9;
      return {px_w:pw,px_h:ph,dest,crop:{x:(w-cw)/2-x*kx,y:(h-ch)/2-y*ky,w:cw,h:ch},pan:{x:kx,y:ky},effective_ppi:eff};
    }
    return {px_w:pw,px_h:ph,dest,crop:{x:(w-cw)*(1-x)/2,y:(h-ch)*(1-y)/2,w:cw,h:ch},effective_ppi:eff};
  }
  function withPanRoom(w,h,p,edit,axes) {
    const l=layout(w,h,p,edit);
    // At cover-fit one axis has no overflow, so its position slider cannot move
    // the image. Add a visible amount of room on the first attempt to pan it.
    const blocked=!l.pan && axes.some(axis=>axis==='x' ? Math.abs(w-l.crop.w)<1 : Math.abs(h-l.crop.h)<1);
    // Below 100% (custom-case only) an exact fit is nudged smaller instead of jumping to 115%.
    const minZoom=p.min_zoom===undefined?1:Number(p.min_zoom);
    return blocked ? {...edit,zoom:edit.zoom<1?Math.max(minZoom,edit.zoom*0.95):Math.min(5,Math.max(1.15,edit.zoom))} : {...edit};
  }
  const core = {crc32,pngDpi,imageHeader,layout,withPanRoom,MAX_BYTES,MAX_PIXELS};
  if (typeof module !== 'undefined' && module.exports) module.exports=core;
  if (typeof window === 'undefined') return;
  window.RRTCCustomPrintCore=core;
  if (customElements.get('custom-print-configurator')) return;
  const blobOf = (c,type='image/png',q) => new Promise((resolve,reject) => c.toBlob(b => b ? resolve(b) : reject(Error('error_generate')),type,q));
  const nextFrame = () => new Promise(r => requestAnimationFrame(r));
  let viewerImport;
  class Configurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized=true; this.generation=0; this.ready=false;
      this.controller=new AbortController(); const opts={signal:this.controller.signal};
      this.config=JSON.parse(this.querySelector('[data-custom-print-config]').textContent);
      for(const m of this.config.models) {m.match=m.match.map(s=>s.trim()).filter(Boolean);m.shape ||= this.config.shapes?.[m.shape_key];}
      this.form=document.getElementById(this.dataset.formId);
      this.fileInput=this.querySelector('[data-file]'); this.canvas=this.querySelector('[data-crop-preview]');
      this.errorNode=this.querySelector('[data-error]'); this.statusNode=this.querySelector('[data-status]');
      if (!this.form) { this.error('error_config'); return; }
      if(!window.RRTCPrintFiles||!window.RRTCPrintCart){this.error('error_config');return;}
      this.unregisterCart=window.RRTCPrintCart.register(this);
      const refreshCart=()=>window.RRTCPrintCart.refresh(this);
      window.addEventListener('pageshow',refreshCart,opts);
      document.addEventListener('visibilitychange',()=>{if(document.visibilityState==='visible')refreshCart();},opts);
      this.fileInput.addEventListener('change',()=>this.onFile(this.fileInput.files[0]),opts);
      this.querySelector('[data-remove]').addEventListener('click',()=>this.reset(),opts);
      this.querySelector('[data-camera-overview]').addEventListener('click',()=>this.camera(false),opts);
      this.querySelector('[data-camera-plate]').addEventListener('click',()=>this.camera(true),opts);
      document.addEventListener('submit',e=>this.guard(e),{capture:true,...opts});
      document.addEventListener('click',e=>this.guard(e),{capture:true,...opts});
      this.form.addEventListener('formdata',e=>this.attach(e.formData),opts);
      this.form.addEventListener('change',e=>{if(e.isTrusted&&this.intent?.phase==='intent'&&e.target.closest('.gpo-app'))this.cartResult('error',this.intent);},opts);
      this.addEventListener('change',()=>this.updateLock(),opts);
      this.bindEditor(opts);
      this.bindLayouts(opts);
      document.addEventListener('gpoRenderCompleted',()=>this.syncAddonVariant(),opts);
      this.setVariant();if(this.config.shapes_url&&!this.config.shapes)this.loadShapes();
      if(typeof subscribe==='function' && typeof PUB_SUB_EVENTS!=='undefined') {
        this.unsubscribe=subscribe(PUB_SUB_EVENTS.variantChange,e=>{
          if(String(e.data?.sectionId)===String(this.dataset.section)) this.setVariant(e.data.variant?.id);
        });
        this.unsubscribeCart=subscribe(PUB_SUB_EVENTS.cartUpdate,refreshCart);
      }
      // Theme replaces submit-button contents and toggles aria-disabled on variant changes.
      this.observer=new MutationObserver(()=>this.updateLock());
      this.observer.observe(this.form,{childList:true,subtree:true});
      this.setupViewer().catch(()=>this.viewerError());
      this.updateLock();
    }
    disconnectedCallback() {
      clearTimeout(this.editTimer); this.decoded?.close?.(); this.decoded=null;
      this.generation++; this.controller?.abort(); this.unsubscribe?.(); this.unsubscribeCart?.(); this.observer?.disconnect();this.unregisterCart?.();
      this.outputs=null; this.file=null; this.mv?.remove(); this.mv=null; this.texture=null; this.ready3D=false; this.initialized=false;
    }
    text(k) { return this.config.strings?.[k] || k; }
    error(k) { this.errorKey=k; this.errorNode.hidden=!k; this.errorNode.textContent=k ? this.text(k) : ''; }
    status(k) { this.statusNode.textContent=k ? this.text(k) : ''; }
    field(name) { return this.querySelector(`[data-property="${name}"]`); }
    currentVariant() { return String(this.form.elements.namedItem('id')?.value || this.dataset.variantId || ''); }
    resolveModel(id) {
      const v=this.config.variants.find(v=>String(v.id)===String(id));
      if(!v) throw Error('error_config');
      const list=this.config.models.filter(m=>m.match.every(s=>v.options.includes(s))).sort((a,b)=>b.match.length-a.match.length);
      if(!list.length || (list[1] && list[0].match.length===list[1].match.length)) throw Error('error_config');
      return {model:list[0],variant:v};
    }
    setVariant(id=this.currentVariant()) {
      try {
        const {model,variant}=this.resolveModel(id), before=this.model;
        this.model=model; this.variant=variant;
        this.applyLayout(false);
        this.canvas.style.background=model.print.background==='transparent'?this.plasticColor():'';
        this.querySelector('[data-transparency-note]').hidden=model.print.background!=='transparent';
        this.querySelector('[data-kit-note]').hidden=!variant.options.includes('Expansion Kit only');
        if(this.mv && before?.glb!==model.glb) { this.ready3D=false; this.mv.src=model.glb || ''; this.mv.poster=model.poster || ''; }
        if(before && JSON.stringify([before.model_version,before.print])!==JSON.stringify([model.model_version,model.print]) && this.file) this.onFile(this.file);
        if(this.spec) this.spec.variant_id=String(variant.id);
        this.updateSpec(); this.updateLock();
        if(!this.decoded)this.drawOverlay(this.canvas,true);
        this.syncAddonVariant();
      } catch(e) { this.ready=false; this.error(e.message); }
    }
    syncAddonVariant() {
      // Globo's generic theme selector otherwise reads the installment form.
      // This unnamed mirror precedes that form without adding a second cart id.
      const input=this.querySelector('[data-gpo-product-variant-selector]');
      if(!input || !this.variant)return;
      input.value=String(this.variant.id);
      const section=document.getElementById('MainProduct-'+this.dataset.section);
      for(const price of section?.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale') || [])price.setAttribute('data-gpo-product-unit-price','');
      for(const price of section?.querySelectorAll('.price__sale .price-item--regular') || [])price.setAttribute('data-gpo-product-compare-at-price','');
      input.dispatchEvent(new Event('change',{bubbles:true}));
      // A condition clears hidden checkboxes but Globo can retain their old
      // dropdown captions/prices when the group reappears. Recalculate using
      // the enabled controls, through the same change events as a customer.
      for(const field of this.form.querySelectorAll('.gpo-app input:not(:disabled), .gpo-app select:not(:disabled)')){
        if(field.type==='checkbox' || field.type==='radio' || field.tagName==='SELECT')field.dispatchEvent(new Event('change',{bubbles:true}));
      }
    }
    updateSpec() { if(this.spec) this.field('spec').value=JSON.stringify(this.spec); }
    productionReady() { return this.config.production_approved===true || (this.config.demo===true && ['localhost','127.0.0.1'].includes(location.hostname)); }
    designReady() { return this.productionReady() && this.ready && this.uploads?.original && this.uploads?.print && this.variant?.available!==false && this.form.querySelector('[name="add"]')?.getAttribute('data-custom-print-available')!=='false' && this.currentVariant()===String(this.variant?.id); }
    confirmationsAccepted() { return this.field('rights').checked && this.field('crop').checked; }
    valid() { return this.designReady() && this.confirmationsAccepted(); }
    cartLocked() { return this.intent && (['intent','sending','uncertain'].includes(this.intent.phase)||(this.intent.phase==='added'&&this.intent.id===this.spec?.design_id)); }
    updateLock() {
      if(!this.form)return;
      if(this.errorKey==='error_confirmations' && this.confirmationsAccepted())this.error('');
      // Never enable a sold-out or busy button owned by the theme/Globo.
      // Missing confirmations are explained on click; submission still requires both.
      const ready=this.designReady();
      for(const btn of this.form.querySelectorAll('[type="submit"],button[name="add"],.gpo-clone-atc-button')){
        btn.classList.toggle('custom-print-incomplete',!ready);
        if(!btn.classList.contains('loading'))btn.disabled=!ready || (this.cartLocked()&&this.intent.phase!=='intent');
      }
      if(!this.productionReady()) this.status('not_available');
    }
    reset(keepInput=false) {
      clearTimeout(this.editTimer); this.decoded?.close?.(); this.decoded=null;this.sourceInfo=null;this.sourceBytes=null;
      this.edit={zoom:1,x:0,y:0};this.syncEditor();
      this.generation++; this.ready=false; this.file=null; this.spec=null; this.outputs=null;this.uploads=null;
      if(!keepInput)this.fileInput.value='';
      const filename=this.querySelector('[data-source-name]');filename.textContent='';filename.hidden=true;
      for(const k of ['original','print','preview','spec','id']) this.field(k).value='';
      this.field('rights').checked=false; this.field('crop').checked=false;
      this.canvas.width=560;this.canvas.height=386;this.canvas.getContext('2d').clearRect(0,0,560,386);this.drawOverlay(this.canvas,true);
      this.querySelector('[data-resolution]').hidden=true;
      this.querySelector('[data-pan-hint]').hidden=true;
      this.error('');this.status('locked_message');this.updateLock();this.clearTexture();
      this.dispatchEvent(new CustomEvent('rrtc:design-invalidated',{bubbles:true}));
    }
    bindEditor(opts) {
      for(const axis of ['zoom','x','y']) this.querySelector(`[data-edit=${axis}]`).addEventListener('input',e=>{
        this.edit[axis]=Number(e.target.value);
        if(axis!=='zoom' && this.edit[axis]!==0)this.ensurePanRoom([axis]);
        else if(axis==='zoom' && this.sourceInfo) {
          const l=layout(this.sourceInfo.w,this.sourceInfo.h,this.model.print,this.edit);
          if(!l.pan&&Math.abs(this.sourceInfo.w-l.crop.w)<1)this.edit.x=0;
          if(!l.pan&&Math.abs(this.sourceInfo.h-l.crop.h)<1)this.edit.y=0;
          this.querySelector('[data-pan-hint]').hidden=true;
        }
        this.editChanged();
      },opts);
      this.querySelector('[data-recenter]').addEventListener('click',()=>{this.edit={zoom:1,x:0,y:0};this.querySelector('[data-pan-hint]').hidden=true;this.editChanged();},opts);
      this.canvas.addEventListener('pointerdown',e=>{
        if(!this.decoded || e.button!==0) return;
        e.preventDefault();this.canvas.setPointerCapture(e.pointerId);
        this.drag={id:e.pointerId,x:e.clientX,y:e.clientY,edit:{...this.edit}};
      },opts);
      this.canvas.addEventListener('pointermove',e=>{
        if(this.drag?.id!==e.pointerId || !this.sourceInfo) return;
        const axes=[];
        if(Math.abs(e.clientX-this.drag.x)>2)axes.push('x');
        if(Math.abs(e.clientY-this.drag.y)>2)axes.push('y');
        if(this.ensurePanRoom(axes))this.drag.edit.zoom=this.edit.zoom;
        const l=layout(this.sourceInfo.w,this.sourceInfo.h,this.model.print,this.drag.edit),r=this.canvas.getBoundingClientRect();
        const sx=this.sourceInfo.w-l.crop.w,sy=this.sourceInfo.h-l.crop.h;
        if(l.pan){this.edit.x=Math.max(-1,Math.min(1,this.drag.edit.x+(e.clientX-this.drag.x)/r.width*l.crop.w/l.pan.x));
          this.edit.y=Math.max(-1,Math.min(1,this.drag.edit.y+(e.clientY-this.drag.y)/r.height*l.crop.h/l.pan.y));this.editChanged();return;}
        this.edit.x=Math.abs(sx)>1e-6?Math.max(-1,Math.min(1,this.drag.edit.x+2*(e.clientX-this.drag.x)/r.width*l.crop.w/sx)):0;
        this.edit.y=Math.abs(sy)>1e-6?Math.max(-1,Math.min(1,this.drag.edit.y+2*(e.clientY-this.drag.y)/r.height*l.crop.h/sy)):0;
        this.editChanged();
      },opts);
      for(const name of ['pointerup','pointercancel','lostpointercapture']) this.canvas.addEventListener(name,()=>{this.drag=null;},opts);
    }
    ensurePanRoom(axes) {
      if(!this.sourceInfo || !axes.length)return false;
      const next=withPanRoom(this.sourceInfo.w,this.sourceInfo.h,this.model.print,this.edit,axes);
      const changed=next.zoom!==this.edit.zoom;
      this.edit=next;
      if(changed)this.querySelector('[data-pan-hint]').hidden=false;
      return changed;
    }
    syncEditor() {
      for(const axis of ['zoom','x','y']) {
        const el=this.querySelector(`[data-edit=${axis}]`);if(el) el.value=this.edit?.[axis] ?? (axis==='zoom'?1:0);
      }
      const out=this.querySelector('[data-zoom-value]');if(out)out.textContent=Math.round((this.edit?.zoom || 1)*100)+'%';
      const group=this.querySelector('[data-editor]');if(group)group.disabled=!this.decoded;
    }
    invalidateEdit() {
      clearTimeout(this.editTimer);this.generation++;this.ready=false;this.spec=null;this.outputs=null;this.uploads=null;
      for(const k of ['original','print','preview','spec','id'])this.field(k).value='';
      this.field('crop').checked=false;this.status('preparing');this.error('');this.updateLock();
      this.dispatchEvent(new CustomEvent('rrtc:design-invalidated',{bubbles:true}));
    }
    compose(canvas,l) {
      const ctx=canvas.getContext('2d',{colorSpace:'srgb'}),p=this.model.print;
      const triangles=this.model.shape?.triangles;
      if(!triangles?.length || Number(p.margin_mm)!==0 || (p.background!=='transparent' && !/^#[0-9a-f]{6}$/i.test(p.background))) throw Error('error_config');
      ctx.clearRect(0,0,canvas.width,canvas.height);ctx.save();ctx.beginPath();
      for(const t of triangles) {
        if(t.length!==3 || t.some(v=>v.length!==2 || v.some(n=>!Number.isFinite(n)||n<0||n>1)))throw Error('error_config');
        ctx.moveTo(t[0][0]*canvas.width,t[0][1]*canvas.height);
        ctx.lineTo(t[1][0]*canvas.width,t[1][1]*canvas.height);ctx.lineTo(t[2][0]*canvas.width,t[2][1]*canvas.height);ctx.closePath();
      }
      ctx.clip();
      if(p.background!=='transparent'){ctx.fillStyle=p.background;ctx.fillRect(0,0,canvas.width,canvas.height);}
      ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality='high';
      const iw=this.decoded.width || this.decoded.naturalWidth, ih=this.decoded.height || this.decoded.naturalHeight;
      const src=this.sourceInfo,c=l.crop;
      if(c.x>=0&&c.y>=0&&c.x+c.w<=src.w+1e-6&&c.y+c.h<=src.h+1e-6)
        ctx.drawImage(this.decoded,c.x/src.w*iw,c.y/src.h*ih,c.w/src.w*iw,c.h/src.h*ih,0,0,canvas.width,canvas.height);
      else { // image smaller than the area (custom-case): draw only its real pixels, the rest stays transparent
        const x0=Math.max(0,c.x),y0=Math.max(0,c.y),x1=Math.min(src.w,c.x+c.w),y1=Math.min(src.h,c.y+c.h);
        if(x1>x0&&y1>y0)ctx.drawImage(this.decoded,x0/src.w*iw,y0/src.h*ih,(x1-x0)/src.w*iw,(y1-y0)/src.h*ih,
          (x0-c.x)/c.w*canvas.width,(y0-c.y)/c.h*canvas.height,(x1-x0)/c.w*canvas.width,(y1-y0)/c.h*canvas.height);
      }
      ctx.restore();
    }
    editChanged() {
      this.syncEditor();if(!this.decoded)return;
      this.invalidateEdit();const job=this.generation;
      try {
        const l=layout(this.sourceInfo.w,this.sourceInfo.h,this.model.print,this.edit);
        this.canvas.width=560;this.canvas.height=Math.round(560*l.px_h/l.px_w);this.compose(this.canvas,l);
        this.textureFromCanvas(this.canvas).catch(()=>this.viewerError());
        this.drawOverlay(this.canvas);
        this.editTimer=setTimeout(()=>this.renderDesign(job),180);
      } catch(e) {this.error(e.message);}
    }
    async onFile(file) {
      this.reset(true); if(!file) return;
      const filename=this.querySelector('[data-source-name]');filename.textContent=file.name;filename.hidden=false;
      this.file=file; const job=++this.generation; this.status('preparing');
      let image;
      try {
        if(file.size>MAX_BYTES) throw Error('error_size');
        const bytes=await file.arrayBuffer(), head=imageHeader(bytes);
        if(!head.w || !head.h || head.w*head.h>MAX_PIXELS || Math.max(head.w,head.h)>20000) throw Error('error_pixels');
        const swap=head.orientation>=5,w=swap?head.h:head.w,h=swap?head.w:head.h;
        // A bounded decoded working image keeps editing memory predictable on mobile.
        // Record this resolution and include it in the effective-PPI warning.
        const scale=Math.min(1,4096/Math.max(w,h));
        if(typeof createImageBitmap==='function') {
          try {image=await createImageBitmap(file,{imageOrientation:'from-image',resizeWidth:Math.max(1,Math.round(w*scale)),resizeHeight:Math.max(1,Math.round(h*scale)),resizeQuality:'high'});} catch(_) {}
        }
        if(!image) {
          const url=URL.createObjectURL(file);
          try {image=new Image();image.src=url;await image.decode();} finally {URL.revokeObjectURL(url);}
        }
        const sourceHash=await hash(bytes);if(job!==this.generation)return;
        this.decoded=image;image=null;this.sourceBytes=bytes;
        this.sourceInfo={name:file.name,type:head.type,bytes:file.size,w,h,raw_w:head.w,raw_h:head.h,sha256:sourceHash,reencoded:false,
          orientation:head.orientation,orientation_applied:true,working_w:this.decoded.width || this.decoded.naturalWidth,working_h:this.decoded.height || this.decoded.naturalHeight};
        this.syncEditor();await this.renderDesign(job);
      } catch(e) {if(job===this.generation){this.ready=false;this.error(e.message.startsWith('error_')?e.message:'error_decode');this.status('');this.updateLock();}}
      finally {image?.close?.();}
    }
    async renderDesign(job) {
      if(job!==this.generation || !this.decoded)return;
      const p=this.model.print,src=this.sourceInfo,edit={...this.edit};let master;
      try {
        const l=layout(src.w,src.h,p,edit);
        master=document.createElement('canvas');master.width=l.px_w;master.height=l.px_h;this.compose(master,l);
        this.canvas.width=560;this.canvas.height=Math.round(560*l.px_h/l.px_w);
        this.canvas.getContext('2d',{colorSpace:'srgb'}).drawImage(master,0,0,this.canvas.width,this.canvas.height);
        this.drawOverlay(this.canvas);
        const raw=await blobOf(master),png=pngDpi(await raw.arrayBuffer(),Number(p.dpi));
        if(png.byteLength>MAX_BYTES)throw Error('error_generate');
        const outputHash=await hash(png);if(job!==this.generation)return;
        const design='CP-'+crypto.randomUUID(),shape=this.model.shape;
        this.spec={v:3,design_id:design,variant_id:String(this.variant.id),model_version:this.model.model_version,
          template_version:'2.3.0',print_profile_id:this.model.profile_version,production_approved:this.config.production_approved===true,
          print_workflow:this.config.print_workflow || 'demo',
          plate:{w_mm:Number(p.w_mm),h_mm:Number(p.h_mm),margin_mm:0,bleed_mm:0},
          shape:{id:shape.id,sha256:shape.sha256},...(this.config.layouts?{print_layout:this.layoutEffective||'full'}:{}),
          print:{dpi:Number(p.dpi),px_w:l.px_w,px_h:l.px_h,color:'sRGB',format:'png',opaque:false,background:p.background,alpha_outside_shape:true},
          source:{...src},output:{sha256:outputHash},fit:{mode:l.pan?'free-xy':'cover-xy',edit,crop:l.crop,destination_px:l.dest},
          effective_ppi:l.effective_ppi*Math.min(src.working_w/src.w,src.working_h/src.h),created:new Date().toISOString(),renderer:'browser-canvas-v2',
          regeneration:'Geometric reproduction; browser and Pillow resampling are not byte-identical.'};
        this.outputs={original:new File([this.sourceBytes],src.name,{type:src.type}),print:new File([png],`print_${design}_${p.dpi}dpi.png`,{type:'image/png'})};
        const [original,print]=await Promise.all([window.RRTCPrintFiles.packageFile(this.outputs.original,src.type==='image/png'?'original.png':'original.jpg'),window.RRTCPrintFiles.packageFile(this.outputs.print,'print.png')]);
        if(job!==this.generation)return;
        this.uploads={original:original.file,print:print.file};
        this.spec.storage={method:'zip-store-v1',original:{entry:original.entry,bytes:original.bytes,sha256:original.sha256},print:{entry:print.entry,bytes:print.bytes,sha256:print.sha256}};
        this.field('id').value=design;this.updateSpec();this.populateInputs();this.ready=true;
        this.querySelector('[data-resolution]').hidden=this.spec.effective_ppi>=150;
        this.status('ready_message');this.updateLock();
        await this.applyTexture().catch(()=>this.viewerError());
        if(job!==this.generation)return;
        this.capturePreview(job).catch(()=>{});
        this.dispatchEvent(new CustomEvent('rrtc:design-ready',{bubbles:true,detail:{designId:design}}));
      } catch(e) {if(job===this.generation){this.ready=false;this.error(e.message.startsWith('error_')?e.message:'error_generate');this.status('');this.updateLock();}}
      finally {if(master){master.width=1;master.height=1;}}
    }
    populateInputs() {
      let populated=true;
      for(const [key,file] of Object.entries({...this.uploads,preview:this.outputs?.preview})) {
        if(!file)continue;
        try { const dt=new DataTransfer();dt.items.add(file);this.field(key).files=dt.files; }
        catch (_) { populated=false; }
      }
      if(!populated && !('FormDataEvent' in window)) throw Error('error_upload_support');
    }
    attach(fd) {
      if(!this.ready || !this.outputs) return;
      // set() prevents duplicate entries when DataTransfer already populated these inputs.
      fd.set('properties[Your image]',this.uploads.original,this.uploads.original.name);
      fd.set('properties[_Print file]',this.uploads.print,this.uploads.print.name);
      if(this.outputs.preview) fd.set('properties[Design preview]',this.outputs.preview,this.outputs.preview.name);
      else fd.delete('properties[Design preview]');
      fd.set('properties[_Print spec]',JSON.stringify(this.spec));
      if(this.layoutLabel)fd.set('properties[Print layout]',this.layoutLabel);
      if(this.intent)fd.set('properties[_Print attempt]',this.intent.attempt);
    }
    guard(e) {
      if(e.type==='click'){
        const button=e.target.closest?.('button,input[type="submit"],.gpo-clone-atc-button,.gpo-clone-stickyAtc-button');
        if(!button||(!this.form.contains(button)&&button.form!==this.form&&!button.matches('.gpo-clone-stickyAtc-button'))||(!button.matches('[type="submit"],[name="add"],.gpo-clone-atc-button,.gpo-clone-stickyAtc-button')&&button.type!=='submit'))return;
      }else if(e.target!==this.form)return;
      if(this.currentVariant()!==String(this.variant?.id)) this.setVariant();
      const localInspection=this.config.demo===true&&['localhost','127.0.0.1'].includes(location.hostname)&&!this.form.hasAttribute('action');
      const followupSubmit=e.type==='submit'&&this.intent?.phase==='intent'&&!this.intent.submitted;
      if(!localInspection&&this.cartLocked()&&!followupSubmit){e.preventDefault();e.stopImmediatePropagation();return;}
      if(!this.valid()) {
        e.preventDefault();e.stopImmediatePropagation();
        this.error(!this.productionReady()?'not_available':!this.designReady()?'locked_message':'error_confirmations');
        if(this.errorKey==='error_confirmations'){
          const missing=['crop','rights'].map(k=>this.field(k)).find(field=>!field.checked);
          missing?.focus({preventScroll:true});
        }
        this.errorNode.scrollIntoView({block:'nearest',behavior:'smooth'}); return;
      }
      // No asynchronous work or redispatch: theme and Globo receive exactly one submit.
      try { this.updateSpec(); this.populateInputs(); this.error(''); } catch (_) { e.preventDefault();e.stopImmediatePropagation();this.ready=false;this.error('error_upload_support');this.updateLock(); }
      if(e.defaultPrevented)return;
      if(!localInspection){
        if(!followupSubmit){
          this.intent={id:this.spec.design_id,variant:this.currentVariant(),generation:this.generation,phase:'intent',attempt:crypto.randomUUID()};
          const intent=this.intent;clearTimeout(this.intentTimer);
          this.intentTimer=setTimeout(()=>{if(this.intent===intent&&intent.phase==='intent')this.cartResult('error',intent);},45000);
        }
        if(e.type==='submit')this.intent.submitted=true;
      }
    }
    cartProperties(){return {'Your image':this.uploads.original,'_Print file':this.uploads.print,...(this.outputs.preview?{'Design preview':this.outputs.preview}:{}),'_Print spec':JSON.stringify(this.spec),'Design ID':this.spec.design_id,'_Print attempt':this.intent.attempt,'Crop reviewed':'Yes','Rights confirmed':'Yes',...(this.layoutLabel?{'Print layout':this.layoutLabel}:{})};}
    cartUpdated(cart,snapshot){
      // Only a complete, successful cart read may release an acknowledged add.
      // An add response contains only the new lines, not the whole cart.
      if(this.intent!==snapshot || snapshot?.phase!=='added' || !Array.isArray(cart?.items) ||
        !Number.isSafeInteger(cart.item_count) || cart.item_count<0 ||
        !cart.items.every(line=>line && line.variant_id!=null && Number.isSafeInteger(line.quantity) && line.quantity>0) ||
        cart.items.reduce((sum,line)=>sum+line.quantity,0)!==cart.item_count)return false;
      if(cart.items.some(line=>line.properties?.['Design ID']===snapshot.id && String(line.variant_id)===snapshot.variant))return false;
      this.intent=null;
      const recovery=this.querySelector('[data-cart-recovery]');if(recovery)recovery.hidden=true;
      this.status(this.ready?'ready_message':'locked_message');this.updateLock();
      return true;
    }
    cartResult(state,snapshot){
      if(state==='busy')return;
      clearTimeout(this.intentTimer);
      if(state==='error'){this.intent=null;this.error('error_cart');}
      else{if(snapshot){snapshot.phase=state;this.intent=snapshot;}this.status(state==='added'?'cart_added':'cart_uncertain');}
      const recovery=this.querySelector('[data-cart-recovery]');if(recovery)recovery.hidden=!['added','uncertain'].includes(state);
      if(state!=='added')for(const b of this.form.querySelectorAll('.loading'))b.classList.remove('loading');
      this.updateLock();
    }
    async setupViewer() {
      if(!this.model.glb) { this.viewerError();return; }
      if(!customElements.get('model-viewer')) {
        viewerImport ||= import(new URL(this.dataset.modelViewerSrc, document.baseURI).href).then(module=>{window.__RRTC_CP_VIEWER_CTOR=module.ModelViewerElement;return module;}); await viewerImport;
      }
      const ctor=customElements.get('model-viewer');
      if(ctor!==window.__RRTC_CP_VIEWER_CTOR || !ctor?.prototype.createCanvasTexture) throw Error('Incompatible existing model-viewer');
      // Local harness uses the bundled Draco decoder; production leaves the versioned default.
      this.mv=document.createElement('model-viewer');
      if(this.dataset.dracoSrc) ctor.dracoDecoderLocation=new URL(this.dataset.dracoSrc, document.baseURI).href;
      const attrs={loading:'eager','camera-controls':'','disable-pan':'','interaction-prompt':'none','min-camera-orbit':'auto 0deg 40%',
        'max-camera-orbit':'auto 180deg 250%','field-of-view':'30deg','environment-image':'neutral','tone-mapping':'neutral',
        exposure:'1','shadow-intensity':'.7','shadow-softness':'1',alt:this.text('viewer_alt')};
      for(const [k,v] of Object.entries(attrs))this.mv.setAttribute(k,v);
      this.mv.addEventListener('load',()=>{this.ready3D=true;this.texture=null;this.camera(false);this.applyTexture().then(()=>this.capturePreview(this.generation)).catch(()=>this.viewerError());});
      this.mv.addEventListener('error',()=>this.viewerError());
      this.querySelector('[data-viewer]').append(this.mv);this.mv.src=this.model.glb;this.mv.poster=this.model.poster || '';
    }
    async loadShapes() {
      // RRTC custom-case: shapes come from config.shapes_url. The cart guard is already registered
      // (no design => no add to cart); uploads wait until the shapes are here.
      this.fileInput.disabled=true;
      try{
        const r=await fetch(this.config.shapes_url,{credentials:'same-origin'});
        if(!r.ok)throw Error('error_config');
        const shapes=await r.json();if(!this.initialized)return;
        this.config.shapes=shapes;
      }catch(_){if(this.initialized)this.error('error_config');return;}
      for(const m of this.config.models)m.shape ||= this.config.shapes[m.shape_key];
      this.fileInput.disabled=false;
      this.setVariant();
    }
    bindLayouts(opts) {
      // RRTC custom-case: 'Full back / Panel' for models that have a <shape_key>-panel shape.
      this.layoutId='full';
      const box=this.querySelector('[data-layouts]'); if(!box||!this.config.layouts)return;
      for(const r of box.querySelectorAll('input[type=radio]'))r.addEventListener('change',()=>{if(r.checked){this.layoutId=r.value;this.applyLayout(true);}},opts);
    }
    applyLayout(redraw) {
      const box=this.querySelector('[data-layouts]'),field=this.field('layout');
      if(!this.config.layouts||!this.model||!box){this.layoutLabel=undefined;return;}
      const base=this.model.shape_key,avail=this.config.layouts.filter(l=>this.config.shapes?.[base+l.suffix]);
      const pick=avail.find(l=>l.id===this.layoutId)||avail[0];
      if(pick)this.model.shape=this.config.shapes[base+pick.suffix];
      this.layoutEffective=pick?.id||'full';
      const choice=avail.length>1; box.hidden=!choice;
      for(const r of box.querySelectorAll('input[type=radio]'))r.checked=r.value===this.layoutEffective;
      this.layoutLabel=choice?(box.querySelector(`input[value="${this.layoutEffective}"]`)?.closest('label')?.textContent.trim()||this.layoutEffective):undefined;
      if(field){field.disabled=!choice;field.value=this.layoutLabel||'';}
      if(redraw){if(this.decoded)this.editChanged();else this.drawOverlay(this.canvas,true);}
    }
    drawOverlay(canvas,empty=false) {
      // RRTC custom-case: display-only guide on the flat proof (case edge, camera openings, print limit).
      const o=this.model?.shape?.overlay; if(!o)return;
      if(empty){canvas.width=560;canvas.height=Math.round(560*Number(this.model.print.h_mm)/Number(this.model.print.w_mm));}
      const ctx=canvas.getContext('2d'),W=canvas.width,H=canvas.height;
      const path=rings=>{const p=new Path2D();for(const r of rings){r.forEach(([u,v],i)=>i?p.lineTo(u*W,v*H):p.moveTo(u*W,v*H));p.closePath();}return p;};
      ctx.save();
      const outside=new Path2D();outside.rect(0,0,W,H);outside.addPath(path([o.body]));
      ctx.fillStyle='#1a1a24';ctx.fill(outside,'evenodd');
      ctx.fillStyle='#0b0c10';ctx.fill(path(o.openings));
      ctx.lineWidth=1.5;ctx.strokeStyle='rgba(245,245,247,.6)';ctx.stroke(path([o.body]));ctx.stroke(path(o.openings));
      ctx.setLineDash([5,4]);ctx.lineWidth=1.2;ctx.strokeStyle='rgba(0,174,66,.95)';ctx.stroke(path(o.printable));
      ctx.restore();
    }
    viewerError() { const el=this.querySelector('[data-viewer-error]');el.hidden=false;el.textContent=this.text('error_webgl'); }
    camera(plate) {
      if(!this.mv) return;
      const c=this.model.camera;
      this.mv.cameraOrbit=plate?c.plate:(matchMedia('(max-width:749px)').matches?c.overview_mobile:c.overview);
      this.mv.cameraTarget=plate?c.target:'auto auto auto';this.mv.fieldOfView=c.fov;
    }
    clearTexture() {
      if(!this.texture) return;
      const c=this.texture.source.element,ctx=c.getContext('2d');ctx.fillStyle=this.plasticColor();ctx.fillRect(0,0,c.width,c.height);this.texture.source.update();
    }
    plasticColor() {
      const color=this.model?.plastic_preview_srgb;
      return /^#[0-9a-f]{6}$/i.test(color) ? color : '#94989f';
    }
    async applyTexture() {
      if(!this.ready3D || !this.ready)return;
      const job=this.generation,model=this.model;
      const bmp=await createImageBitmap(this.outputs.print);
      try {if(job===this.generation && model===this.model)await this.textureFromCanvas(bmp);}finally{bmp.close();}
    }
    async textureFromCanvas(source) {
      if(!this.ready3D)return;
      const mat=this.mv.model.getMaterialByName(this.model.material);if(!mat)throw Error('Missing PRINT_AREA');
      this.texture ||= this.mv.createCanvasTexture();
      const c=this.texture.source.element;c.width=1024;c.height=Math.round(1024*this.model.print.h_mm/this.model.print.w_mm);
      const ctx=c.getContext('2d',{colorSpace:'srgb'});
      // Transparent ink shows the plastic in 3D; it must not make a hole in the
      // mesh or become an added white/black band. This underlay is PREVIEW ONLY.
      ctx.fillStyle=this.plasticColor();ctx.fillRect(0,0,c.width,c.height);
      // Compensate CanvasTexture flipY only at the GPU boundary; the PNG remains upright.
      ctx.save();ctx.translate(0,c.height);ctx.scale(1,-1);ctx.drawImage(source,0,0,c.width,c.height);ctx.restore();
      this.texture.source.update();this.texture.sampler.setWrapS(33071);this.texture.sampler.setWrapT(33071);
      mat.pbrMetallicRoughness.baseColorTexture.setTexture(this.texture);await nextFrame();
    }
    async capturePreview(job) {
      if(!this.ready3D || !this.outputs) return;
      const blob=await Promise.race([this.mv.toBlob({mimeType:'image/jpeg',quality:.82}),new Promise((_,reject)=>setTimeout(()=>reject(Error('timeout')),3000))]);
      if(job!==this.generation || !blob) return;
      this.outputs.preview=new File([blob],`preview_${this.spec.design_id}.jpg`,{type:'image/jpeg'});this.populateInputs();
    }
  }
  customElements.define('custom-print-configurator',Configurator);
})();
